import { Stack, styled } from "@mui/material";
import {
  Attachment,
  Row,
  SmText,
  TextMd,
  ViewAllText,
  XsText,
} from "Components";
import { ComplaintButton, GreenButton, ReviewButton } from "Components/button/index";
import Card from "Components/card/Card";
import { ALink } from "Components/link";
import { StarRating } from "Components/rating";
import { COLORS } from "constants/layout";
import {
  DIRECT_PATHS,
  SEARCH_PARAM_VALUES,
  SEARCH_PARAMS,
} from "constants/routes";
import dayjs from "dayjs";
import useGetData from "hooks/useGetData";
import { useTranslation } from "hooks/useTranslation";
import { memo, useMemo, useState } from "react";
import { apiGetReviews } from "services/api/review";
import { ReviewType } from "types/review";
import { LikeIcon } from "Assets/icons";
import { useHistory } from "react-router-dom";
import { objectToQueryString } from "helpers/search";

type Props = {
  viewAll?: boolean;
  type?: string;
  title?: string;
  reference_name?: string;
  onComplete?: () => void;
};

const Reviews = (props: Props) => {
  const t = useTranslation();
  const { viewAll, type, title, reference_name, onComplete } = props;
  const history = useHistory();
  const [reload, setReload] = useState(false);

  const { data } = useGetData({
    promise: () =>
      reference_name
        ? apiGetReviews({
            review_type: "Supplier",
            reference_name,
            limit: viewAll ? undefined : 5,
          })
        : null,
    deps: [reference_name, reload, viewAll],
  });

  const reviews: ReviewType[] = useMemo(() => data?.data || [], [data]);

  const onReloadReview = () => {
    setReload(!reload);
    onComplete?.();
  };

  const onClickViewAll = () => {
    history.push({
      pathname: DIRECT_PATHS.supplierDetail(reference_name),
      search: objectToQueryString({
        [SEARCH_PARAMS.tab]: SEARCH_PARAM_VALUES.tabReview,
      }),
    });
  };

  const onClickReview = () => {
    const reviewButton = document.getElementById("review-button");
    if (reviewButton) reviewButton.click();
  };

  return (
    <Card>
      <Row justifyContent="space-between">
        <TextMd>{t("user_reviews")}</TextMd>
        {!viewAll && <ViewAllText onClick={onClickViewAll} />}
      </Row>
      {reviews.length === 0 && (
        <Stack alignItems="center">
          <SmText align="center" mb={2}>
            {t("no_recent_review")}
          </SmText>
          <GreenButton
            startIcon={<LikeIcon fill="#fff" />}
            onClick={onClickReview}
          >
            {t("write_a_review")}
          </GreenButton>
        </Stack>
      )}

      {reviews.map((review, i) => {
        const {
          reviewer_name,
          reviewer_id,
          content,
          creation,
          rating,
          attachments = [],
        } = review;

        return (
          <Stack
            key={i}
            p={2}
            borderRadius={2}
            spacing={1}
            border={`1px solid ${COLORS.gray[200]}`}
          >
            <Row justifyContent="space-between">
              {reviewer_name && (
                <ALink href={DIRECT_PATHS.buyerDetail(reviewer_id)}>
                  <XsText color={COLORS.green[600]}>{reviewer_name}</XsText>
                </ALink>
              )}
              <XsText color={COLORS.gray[500]}>
                {dayjs(creation).format("DD MMMM YYYY")}
              </XsText>
            </Row>

            <StarRating value={rating} readOnly />

            {content && <XsText>{content}</XsText>}

            <Stack gap={1} direction="row" flexWrap="wrap">
              {attachments?.map((media, i) => (
                <Attachment attachment={media.url} key={i} />
              ))}
            </Stack>
          </Stack>
        );
      })}

      <StyledButtons justifyContent="flex-end">
        <ReviewButton
          {...{ type, title, reference_name }}
          onComplete={onReloadReview}
        />
        <ComplaintButton {...{ type, title, reference_name, onComplete }} />
      </StyledButtons>
    </Card>
  );
};

const StyledButtons = styled(Row)({
  button: {
    fontWeight: 400,
    fontSize: 12,
  },
});

export default memo(Reviews);
